import React from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {_openModal} from '../reducers/modals';
import {Column, Dropdown, LoadingSpinner} from '../index';
import Table from '../components/Table';
import debug from 'debug';
import {gatherData, getUpdatedFilter} from '../utils/utils';
import useQueryObject from '../utils/useQueryObject';
import {isEntitled} from '../reducers/user';
import {withSize} from 'react-sizeme';
import useSWR from 'swr';
import useQueryString from '../utils/useQueryString';
import {fixHouseholdType, getTransactionTypes, getInstitutions} from '../utils/containerUtils';
import {_getPeriods, getDefaultTimePeriod} from '../reducers/periods';
import moment from 'moment';
import lazy from 'lazy.js';

const log = debug('circleblack:advisor:analyze:transactions:table');

const TransactionsTable = ({
  sessionRole,
  container,
  openModal,
  size,
  onRowClick,
  rowsPerPage,
  periods,
  getPeriods,
  fullPage,
}) => {
  const {data: con} = useSWR(
    container && `/containers/${container.id}`,
    null,
    {fallbackData: container}
  );
  log('con', con);

  const [institutionName] = useQueryString('institutionName');
  const [type] = useQueryString('type');

  React.useEffect(() => {getPeriods();}, []);

  const [period, setPeriod] = useQueryString('transactionPeriod');
  const [timePeriod, setTimePeriod] = React.useState();

  const _periods = React.useMemo(() => con?.start && lazy(periods)
    .filter(p => p.id === 'MAX' || p.start >= con.start)
    .map(p => p.id === 'MAX' ? {
      ...p,
      start: con.start,
      days: moment().diff(moment(con.start, 'YYYY-MM-DD'), 'd'),
    } : p)
    .sortBy('days')
    .toArray(), [con, periods]);

  React.useEffect(() => {
    if (_periods && con) {
      setTimePeriod(_periods.find(p => p.id === period) || getDefaultTimePeriod(null, con.start));
    }
  }, [_periods, con]);

  React.useEffect(() => {
    if (timePeriod) {
      setPeriod(timePeriod.id);
    }
  }, [timePeriod]);

  const [filters, setFilters] = useQueryObject(
    'transactionTableFilters',
    {
      type,
      institutionName,
    }
  );
  const gatherTransactions = con && gatherData(`/containers/${con.id}/transactions`);

  const dataManager = {
    gatherData: inputValue => gatherTransactions({
      ...inputValue,
      ...getUpdatedFilter(filters),
      start: timePeriod && timePeriod.start || moment().format('YYYY-MM-DD'),
    }),
  };

  React.useEffect(() => {
    if (dataManager.refresh) {
      dataManager.refresh();
    }
  }, [con, filters, timePeriod]);

  const {data: types} = getTransactionTypes({
    con,
    options: {
      onError: err => openModal({type: 'Error', title: 'Transaction Types', err}),
      onSuccess: input => {
        if (type) {
          setFilters(current => ({...current, type: input.find(i => i.label === type)}));
        }
      },
    },
  });

  // ETODO Update period filter to date filter

  const {data: institutions} = getInstitutions({
    con,
    options: {
      onError: err => openModal({type: 'Error', title: 'Transaction Institutions', err}),
      onSuccess: input => {
        if (institutionName) {
          setFilters(current => ({...current, institutionName: input.find(i => i.label === institutionName)}));
        }
      },
    },
  });

  log('institutions', institutions);

  const updateFilters = (name, value) => setFilters({...filters, [name]: value});

  log('rendering');

  if (!con) {
    return <LoadingSpinner/>;
  }

  return <Column alignItems={'left'} layout={{grow: 1}}>
    {timePeriod && _periods && <Dropdown onChange={(name, value) => setTimePeriod(value.value)}
                                         title={'Time Period'}
                                         options={_periods.filter(p => (p.start >= con.start))
                                           .sort((a, b) => a.days - b.days)
                                           .map(p => ({label: p.name, value: p}))}
                                         dataPoint={{
                                           name: 'timePeriod',
                                           value: {label: timePeriod.name, value: timePeriod},
                                         }}
                                         placeholder={'Select a Period'}/>}
    <Table dataManager={dataManager}
           columns={[
             {
               title: 'Date',
               dataPoint: 'date',
               type: 'date',
               alignment: 'left',
               minWidth: 110,
               isSortable: true,
             },
             (fullPage || size.width >= 540) && {
               title: 'Description',
               dataPoint: 'description',
               type: 'text',
               minWidth: 200,
               isSortable: true,
             },
             (fullPage || size.width >= 340) && {
               title: 'Type',
               dataPoint: 'type',
               type: 'text',
               minWidth: 110,
               isSortable: true,
             },
             (fullPage || size.width >= 710) && container.type !== 'ACCOUNT' && {
               title: 'Account',
               dataPoint: 'accountName',
               type: 'text',
               minWidth: 170,
               isSortable: true,
             },
             (fullPage || size.width > 800) && !['ACCOUNT', 'HOUSEHOLD'].includes(fixHouseholdType(container.type)) && {
               title: 'Household',
               dataPoint: 'householdName',
               type: 'text',
               minWidth: 80,
               isSortable: true,
             },
             (fullPage || size.width >= 880) && container.type !== 'ACCOUNT' && isEntitled([
               {
                 group: 'account',
                 feature: 'institution',
                 access: 'r',
               },
             ])
             && {
               title: isEntitled([{group: 'account', feature: 'aggrManAccInstName', access: 'r'}]) || sessionRole === 'CUSTOMER'
                 ? 'Institution' : 'Custody',
               dataPoint: isEntitled([{group: 'account', feature: 'aggrManAccInstName', access: 'r'}]) || sessionRole === 'CUSTOMER'
                 ? 'institutionName' : 'accountCustody',
               type: 'text',
               minWidth: 170,
               isSortable: true,
             },
             (fullPage || size.width >= 230) && {
               title: 'Amount',
               dataPoint: 'amount',
               type: 'currency',
               minWidth: 120,
               isSortable: true,
             },
           ].filter(c => !!c)}
           defaultSort={{
             column: 'date',
             desc: true,
           }}
           searches={[
             {
               title: 'Transaction Description',
               name: 'description',
               isFuzzy: true,
             },
             container.type !== 'ACCOUNT' && {
               title: 'Account Name',
               name: 'accountName',
               isFuzzy: true,
             },
             !['ACCOUNT', 'HOUSEHOLD'].includes(fixHouseholdType(container.type)) && {
               title: 'Household Name',
               name: 'householdName',
               isFuzzy: true,
             },
           ].filter(s => !!s)}
           rowIdentifier={'id'}
           onRowClick={onRowClick}
           filters={[
             <Dropdown key={'TransactionsTypesFilter'}
                       placeholder={'Select...'}
                       title={'Type'}
                       onChange={updateFilters}
                       isSearchable={false}
                       isClearable={true}
                       options={types}
                       dataPoint={{
                         name: 'type',
                         value: filters.type,
                       }}/>,
             container.type !== 'ACCOUNT' && institutions && <Dropdown key={'TransactionsInstitutionsFilter'}
                                                                       title={isEntitled([
                                                                         {
                                                                           group: 'account',
                                                                           feature: 'aggrManAccInstName',
                                                                           access: 'r',
                                                                         },
                                                                       ]) || sessionRole === 'CUSTOMER' ? 'Institution' : 'Custody'}
                                                                       onChange={updateFilters}
                                                                       isSearchable={true}
                                                                       isClearable={true}
                                                                       options={institutions.map(i => {
                                                                         delete i.subLabel;
                                                                         return i;
                                                                       })}
                                                                       dataPoint={{
                                                                         name: 'institutionName',
                                                                         value: filters.institutionName,
                                                                       }}/>,
           ].filter(f => !!f)}
           rowsPerPage={rowsPerPage}
           filterValues={filters}/>
  </Column>;
};

const mapStateToProps = state => ({
  sessionRole: state.session.profile.role,
  periods: state.periods,
});

const mapDispatchToProps = (dispatch) => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
  getPeriods: () => dispatch(_getPeriods()),
});

export default compose(withSize(), connect(mapStateToProps, mapDispatchToProps))(TransactionsTable);

TransactionsTable.propTypes = {
  sessionRole: PropTypes.shape().isRequired,
  /**
   * SizeMe size
   */
  size: PropTypes.shape({
    width: PropTypes.number,
  }),
  openModal: PropTypes.func.isRequired,
  container: PropTypes.shape({id: PropTypes.number.isRequired, type: PropTypes.string.isRequired}).isRequired,
  /**
   * Optional on row click action
   */
  onRowClick: PropTypes.func,
  /**
   * Optional rows per page override
   */
  rowsPerPage: PropTypes.number,
  /**
   * If isEntitled([{group: 'securities', feature: 'isCashEq', access: 'r'} and true/false filter to only cash or non cash positions
   */
  isCashEq: PropTypes.bool,
  getPeriods: PropTypes.func.isRequired,
  periods: PropTypes.array.isRequired,
  /**
   * Indicates this is a full page and should not have size restrictions
   */
  fullPage: PropTypes.bool,
};
